
import * as THREE from 'three'
import { loadCubeMap, loadGLTF, loadTexture } from './loaderUtils';
import {replaceMaterial} from './utils';


class Scene extends THREE.Scene{
  constructor(config){
    super();
    this.config = config;
    this.rayTargetObjects = [];
    this.rayTargetObjectsInternal = {};
    this.currentViewModels = [];
    this.currentViewName = '';
    this.defaultCameraPosition = new THREE.Vector3();
    this.defaultCameraAngle = new THREE.Vector2();
  }

  async init(){

     // add lights (directional/ambient/directional(sub))
     {
      const shadowBias = -0.0005;

      this.dirLight = new THREE.DirectionalLight( 0xffffff, 1);
      this.dirLight.position.set( -400, 300, 400 );
      this.dirLight.castShadow = true;
      this.dirLight.shadow.camera.near = 1;
      this.dirLight.shadow.camera.far = 1000;
      this.dirLight.shadow.camera.right = 100;
      this.dirLight.shadow.camera.left = -100;
      this.dirLight.shadow.camera.top	= 100;
      this.dirLight.shadow.camera.bottom = -100;
      this.dirLight.shadow.mapSize.width = 1024;
      this.dirLight.shadow.mapSize.height = 1024;
      this.dirLight.shadow.bias = shadowBias;
      this.dirLight.intensity = this.config.dirLightIntensity;
      this.add(this.dirLight);
  
      this.subDirLight = new THREE.DirectionalLight( 0xffffff, 1);
      this.subDirLight.position.set( -400, 300, 400 );
      this.subDirLight.intensity = this.config.subDirLightIntensity;
      this.add(this.subDirLight);
  
      this.ambientLight = new THREE.AmbientLight( 0xffffff );
      this.ambientLight.intensity = this.config.ambientLightIntensity;
      this.add(this.ambientLight);
    }

    // add env objects(cubemap/refrection cubemap)
    {
      this.cubeMap = await loadCubeMap(this.config.cubeMap, '.png');
      this.refCubeMap = await loadCubeMap(this.config.refCubeMap, '.png');
    }

    // add common objects
    {
      this.commonModels = [];
      for(let i=0; i<this.config.commonModels.length; i++){
        const model = await loadGLTF(this.config.commonModels[i]);
        this.add(model.scene);
        this.commonModels.push(model.scene);
      }

      // cursor
      this.cursorTexture = await loadTexture('/assets/ax_cursor.png');
      this.cursorGeom = new THREE.PlaneGeometry();
      this.cursorMaterial = new THREE.MeshBasicMaterial({map: this.cursorTexture, transparent: true});

      this.cursor = new THREE.Mesh( this.cursorGeom, this.cursorMaterial);
      this.cursor.scale.set(1.5, 1.5, 1);
      this.cursor.rotateX(-Math.PI/2);
      this.cursor.visible = false;
      this.add(this.cursor);
    }

    // post process
    {
      for(let i=0; i<this.commonModels.length; i++){
        this.modifySceneMaterials(this.commonModels[i]);
      }
    }
  } // end of init

  modifyBackgroundMaterials(background){
    background.traverse((child)=>{ 
      if(child.isMesh){
        // 床は建物と同じマテリアルを設定し、シャドウを落とす
        if(child.name == 'Ground'){
          replaceMaterial(child, this.config.defaultMaterial);
          child.receiveShadow = true;
          child.movable = true;
          this.rayTargetObjectsInternal[`${child.name}`] = child;
      
        }
        else
          replaceMaterial(child, 'MeshBasicMaterial');
      }
    });

    // 少し下に配置(モデルの床とかぶるので)
    background.position.set(0, -0.1, 0);
  } // end of modifyBackgroundMaterials

  modifySceneMaterials(model){
    model.traverse((child)=>{ 
      if(child.isMesh){
        child.material.emissive = 0;
        child.material.envMapIntensity = this.config.envIntensity;

        if(child.material.opacity < 1){
          child.castShadow = false;
          child.receiveShadow = false;
        }else{
          child.castShadow = true;
          child.receiveShadow = true;
        }
        child.material.envMap = this.cubeMap; 

        if(this.config.defaultMaterial != 'MeshStandardMaterial')
          replaceMaterial(child, this.config.defaultMaterial);

        const attr = child.name.split('-')[1];
        if(attr == 'movable'){
          child.movable = true;
        }

        if(attr == 'touchable'){
          child.touchable = true;
          // this.rayTargetObjectsInternal[`${child.name}`] = child;
        }
        this.rayTargetObjectsInternal[`${child.name}`] = child;
      }
    });
  } // end of modifySceneMaterials

  async loadBackground(){
    const background = await loadGLTF('/assets/background.glb');
    this.background = background.scene;
    this.add(this.background);
    this.modifyBackgroundMaterials(this.background);
  }

  async change(name){
    // 同じなまえのシーンの場合リターン
    if(this.currentViewName == name)
      return false;
    
    const viewInfo = this.config.views[`${name}`];
    // シーンのなまえがconfigに含まれていない場合はリターン
    if(!viewInfo){
      console.error(`${name}はこのシーンに含まれていません`);
      return false;
    }

    // 前のビューのモデルをシーンから削除
    for(let i=0; i<this.currentViewModels.length; i++){
      this.currentViewModels[i].traverse((child)=>{ 
        if(child.isMesh){
          if(child.movable){
            delete this.rayTargetObjectsInternal[`${child.name}`];
          }
        }
      });
      this.remove(this.currentViewModels[i]);
      this.disposeGLTF(this.currentViewModels[i]);
    
    }
    this.currentViewModels = [];
    // 新しいビューのモデルを追加
    for(let i=0; i<viewInfo.models.length; i++){
      const model = await loadGLTF(viewInfo.models[i]);
      this.modifySceneMaterials(model.scene);
      this.add(model.scene);
      this.currentViewModels.push(model.scene);
    }

    // デフォルトのカメラ位置を更新
    this.defaultCameraPosition.x = viewInfo.initCameraPos.x;
    this.defaultCameraPosition.y = viewInfo.initCameraPos.y;
    this.defaultCameraPosition.z = viewInfo.initCameraPos.z;
    this.defaultCameraAngle.x = viewInfo.initCameraAngle.azimuth; 
    this.defaultCameraAngle.y = viewInfo.initCameraAngle.polar; 

    // なまえを更新
    this.currentViewName = name; 

    // raytargetをリセット
    this.rayTargetObjects = [];
    return true;
  } // end of change

  getDefaultCameraPositionBySceneName(name){
    const viewInfo = this.config.views[`${name}`];
    if(!viewInfo){
      console.error(`${name}はこのシーンに含まれていません`);
      return new THREE.Vector3();
    }
    return new THREE.Vector3(viewInfo.initCameraPos.x, viewInfo.initCameraPos.y, viewInfo.initCameraPos.z);
  }

  getDefaultCameraPosition(){
    return this.defaultCameraPosition;
  }

  getDefaultCameraAngleBySceneName(name){
    const viewInfo = this.config.views[`${name}`];
    if(!viewInfo){
      console.error(`${name}はこのシーンに含まれていません`);
      return new THREE.Vector2();
    }
    return new THREE.Vector2(viewInfo.initCameraAngle.azimuth, viewInfo.initCameraAngle.polar);
  }

  getDefaultCameraAngle(){
    return this.defaultCameraAngle;
  }

  disposeGLTF(model){
    model.traverse((child)=>{ 
      if(child.isMesh){
        // マテリアルの差し替えがある場合はreplacedMaterialにもともとあったマテリアルを入れてある
        if(child.replacedMaterial){
          child.replacedMaterial.dispose();
          if(child.replacedMaterial.map)
            child.replacedMaterial.map.dispose();
        }else{
          child.material.dispose();
          if(child.material.map)
            child.material.map.dispose();
        }

        child.geometry.dispose();
      }
    });
  } // end of disposeGLTF

  getRayTargetObjects(){
    if(this.rayTargetObjects.length != 0)
      return this.rayTargetObjects;
    
    this.rayTargetObjects = Object.values(this.rayTargetObjectsInternal);
    return this.rayTargetObjects;
  }

  destroy(){
    // todo dispse all meshes
    
  } // end of destroy

}


export default Scene;
